/**
 * lib/hrPayroll.ts
 * ------------------------------------------------------------------
 * HR PAYROLL DOMAIN — types, the deduction catalog, the versioned
 * Botswana PAYE tax-table model, and the pure tax/gratuity engine.
 *
 * This is the Phase 1 keystone. It extends the existing HR domain
 * (lib/hr.ts) from a "references only" payroll stub into a real
 * payslip-generating engine. It deliberately follows the same
 * conventions as lib/hr.ts and lib/grading.ts:
 *   - all collections prefixed `hr*`
 *   - timestamps are ISO strings
 *   - the math lives in PURE functions so the UI, the pay-run, the
 *     PDF, and any Cloud Function all agree on the numbers
 *
 * NON-NEGOTIABLE compliance points baked in here:
 *   - PAYE is calculated on GROSS earnings (no pre-tax deductions in
 *     Phase 1 scope). See computeAnnualPAYE / monthlyPAYE.
 *   - Gratuity tax = 50% exempt, remaining 50% spread over the
 *     contract years, added to annual salary, taxed at the resulting
 *     marginal rate. See computeGratuityTax. (Botswana practice; LKC
 *     bursar confirms during Phase 1 demo.)
 *   - Every payslip records WHICH tax-table version computed it, and
 *     historical payslips never re-calculate.
 *   - A payslip must reconcile exactly: net = gross - totalDeductions.
 *     (The school's old Odoo sample had broken math; we do not.)
 *
 * Everything here is removable with the rest of the HR domain.
 * ------------------------------------------------------------------ */

/* ==================================================================
   COLLECTION NAMES (new payroll collections)
   ------------------------------------------------------------------
   Kept separate from HR_COLLECTIONS in lib/hr.ts so the two waves of
   the build don't fight over one constant. All still `hr*`.
   ================================================================== */

export const PAYROLL_COLLECTIONS = {
  /** One per staff: basic salary, allowances, gratuity rate, bank. */
  payrollProfile: 'hrPayrollProfile',
  /** One row per deduction instance per staff. */
  deductions: 'hrDeductions',
  /** Every generated payslip, stored immutably. */
  payslips: 'hrPayslips',
  /** Versioned Botswana PAYE tables; exactly one active. */
  taxTables: 'hrTaxTables',
  /** Per-staff per-leave-type annual entitlements. */
  leaveAllocations: 'hrLeaveAllocations',
  /** Bursar-configured allowance types. */
  allowanceCatalog: 'hrAllowanceCatalog',
  /** One row per (staff, contract, month): monthly gratuity accrual. */
  gratuityAccruals: 'hrGratuityAccruals',
  /** One row per gratuity payout event with tax breakdown. */
  gratuityPayouts: 'hrGratuityPayouts',
  /** Singleton school-level config. */
  settings: 'hrPayrollSettings',
  /** Append-only audit of sensitive payroll actions. */
  auditLog: 'hrAuditLog',
} as const;

/** Fixed id for the settings singleton document. */
export const PAYROLL_SETTINGS_ID = 'school';

/* ==================================================================
   MONEY HELPERS
   ------------------------------------------------------------------
   All amounts are BWP. We round to 2 d.p. consistently so the
   pay-run, the PDF, and the totals all reconcile to the thebe.
   ================================================================== */

/** Round to 2 decimal places, avoiding binary-float drift. */
export function round2(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

/** Format a BWP amount the way the payslip shows it (no symbol). */
export function fmtPula(n: number): string {
  return round2(n).toLocaleString('en-BW', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/** Sum a list of {amount} rows, rounded. */
export function sumAmounts(rows: { amount: number }[]): number {
  return round2(rows.reduce((acc, r) => acc + (Number(r.amount) || 0), 0));
}

/* ==================================================================
   ALLOWANCES & PAYROLL PROFILE
   ------------------------------------------------------------------
   Decision #1: basic salary is a plain numeric field (no bands).
   Decision #2/#6: gratuity rate is per-staff (LKC range 15–30%).
   Decision #3: allowances are per-staff; the *catalog* of allowance
   types is bursar-configurable.
   ================================================================== */

export interface AllowanceCatalogEntry {
  id: string;
  /** Display name, e.g. "Housing", "Transport", "Acting". */
  label: string;
  /** Optional default monthly amount when added to a staff member. */
  defaultAmount?: number;
  /** Sort order in the dropdown. */
  order?: number;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface StaffAllowance {
  /** References AllowanceCatalogEntry.label (denormalised for snapshot). */
  type: string;
  amount: number;
}

export interface BankAccountDetails {
  bankName?: string;
  accountName?: string;
  accountNumber?: string;
  branchCode?: string;
}

export interface HrPayrollProfile {
  id: string; // == staffId
  staffId: string;
  /** Plain numeric monthly basic salary (BWP). No bands. */
  basicSalary: number;
  /** Optional free-text label only — drives no math (decision #1). */
  salaryBand?: string;
  allowances: StaffAllowance[];
  /** Per-staff gratuity rate as a PERCENT, e.g. 25 means 25%. */
  gratuityRatePct: number;
  /** Whether this staff member accrues gratuity at all (decision #7). */
  gratuityEligible: boolean;
  /**
   * Migration figure captured at go-live: the TOTAL basic salary already
   * paid in the CURRENT contract before the system took over. Gratuity
   * resets each contract, so the running gratuity base =
   * openingBasicPaidThisContract + basic on every payslip since.
   * Reset to 0 when a new contract starts.
   */
  openingBasicPaidThisContract?: number;
  bank?: BankAccountDetails;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

/* ==================================================================
   DEDUCTION CATALOG (all 13 types)
   ------------------------------------------------------------------
   Data-driven: each type declares its form as a list of FieldDef.
   The Deductions UI renders the dropdown → picks a type → renders
   that type's fields → saves the values into HrDeduction.values.
   Every form implicitly ends with a Custom Fields section (decision:
   "every form ends with custom fields") handled by the UI, stored in
   HrDeduction.customFields — so the bursar can add ad-hoc fields with
   no redeploy.
   ================================================================== */

export type DeductionType =
  | 'medical_aid'
  | 'salary_advance'
  | 'school_loan'
  | 'bank_scheme'
  | 'funeral_cover'
  | 'car_insurance'
  | 'rent'
  | 'electricity'
  | 'water'
  | 'alarms'
  | 'school_fees'
  | 'residence_permit'
  | 'paye'
  | 'other';

/**
 * Loose value type a form field can hold.
 *
 * The two array shapes are not form fields — no input writes them. They are
 * the append-only histories the balance engine reads (see "VARYING INSTALMENTS
 * AND TOP-UPS" below), and they live on `values` because that is the bag a
 * deduction record already carries. Typed here rather than cast at each use so
 * that a malformed history is a compile error and not a silent nil.
 */
export type FieldValue =
  | string | number | boolean | null
  | { fromMonth: string; amount: number; note?: string; by?: string; at?: string }[]
  | { month: string; amount: number; note?: string; by?: string; at?: string }[];

export type FieldKind =
  | 'text'
  | 'textarea'
  | 'number'
  | 'money'
  | 'date'
  | 'month'
  | 'select'
  | 'checkbox';

export interface FieldDef {
  /** Stored under HrDeduction.values[key]. */
  key: string;
  label: string;
  kind: FieldKind;
  /** For select kinds: the seed options. */
  options?: string[];
  /** For select kinds: bursar may extend the option list (stored at
   *  school level under settings.dropdowns[key]). */
  editableOptions?: boolean;
  required?: boolean;
  /** Help text shown under the field. */
  hint?: string;
  /** A value the field carries by default when the form opens. */
  default?: FieldValue;
  /** Marks a field as auto-computed (read-only unless overridden). */
  computed?: boolean;
}

export type DeductionGroup =
  | 'statutory'
  | 'benefit'
  | 'loan'
  | 'utility'
  | 'housing'
  | 'other';

export interface DeductionTypeConfig {
  type: DeductionType;
  label: string;
  group: DeductionGroup;
  /** Lucide icon name (resolved to a component in the UI layer). */
  icon: string;
  /** Short description shown in the "add deduction" picker. */
  blurb: string;
  /** The values[] key holding the monthly amount deducted per pay run.
   *  PAYE has none here (it is computed by the engine each run). */
  amountKey: string | null;
  /** If this deduction tracks a running balance shown on the payslip,
   *  the values[] key holding it. */
  balanceKey?: string;
  /** Whether the remaining balance prints on the payslip. */
  showBalanceOnPayslip?: boolean;
  /** The form fields (custom-fields section is appended by the UI). */
  fields: FieldDef[];
}

const DATE_FIELDS_TAIL: FieldDef[] = [
  { key: 'firstMonth', label: 'First month', kind: 'month', required: true },
  { key: 'endDate', label: 'End month (optional)', kind: 'month' },
  { key: 'notes', label: 'Notes', kind: 'textarea' },
];

/** The canonical 13-type catalog. Codes are CLEAN (we do not reproduce
 *  the messy upper/lower-case duplicates from the old Odoo payslip). */
export const DEDUCTION_TYPES: Record<DeductionType, DeductionTypeConfig> = {
  medical_aid: {
    type: 'medical_aid',
    label: 'Medical Aid',
    group: 'benefit',
    icon: 'HeartPulse',
    blurb: 'Medical scheme contribution, split with the school.',
    amountKey: 'employeeContribution',
    fields: [
      { key: 'plan', label: 'Plan / option', kind: 'text' },
      { key: 'membershipNumber', label: 'Membership number', kind: 'text' },
      { key: 'dependants', label: 'Dependants count', kind: 'number', default: 0 },
      { key: 'monthlyPremium', label: 'Total monthly premium', kind: 'money', required: true },
      {
        key: 'employeeContribution',
        label: 'Employee contribution',
        kind: 'money',
        hint: 'Defaults to 50% of the premium; editable.',
        computed: true,
      },
      {
        key: 'employerContribution',
        label: 'Employer contribution',
        kind: 'money',
        hint: 'The remaining share; read-only.',
        computed: true,
      },
      ...DATE_FIELDS_TAIL,
    ],
  },

  /* ONE PAYROLL, NEVER SPREAD (the school, 26 Oct 2026). An advance comes
     off whole in the first payroll not yet done — this month's, or next
     month's once this month's has been run. So the amount recovered IS the
     advance (amountKey loanAmount: the engine takes the smaller of that and
     what is left, i.e. all of it, once), there is no monthly figure or
     instalment count to set, and the month is fixed by the form, not chosen.
     Every advance on file was already a single instalment. */
  salary_advance: {
    type: 'salary_advance',
    label: 'Salary Advance',
    group: 'loan',
    icon: 'HandCoins',
    blurb: 'Salary advance recovered in full from one payroll (no interest).',
    amountKey: 'loanAmount',
    balanceKey: 'remainingBalance',
    showBalanceOnPayslip: true,
    fields: [
      { key: 'loanAmount', label: 'Advance amount', kind: 'money', required: true },
      { key: 'reason', label: 'Reason / purpose', kind: 'text' },
      { key: 'advanceDate', label: 'Date granted', kind: 'date' },
      { key: 'firstRepaymentMonth', label: 'Deducted from pay in', kind: 'month', required: true, computed: true, hint: "The whole advance comes off one payroll: this month's, or next month's if this month's payroll has already been done." },
      { key: 'remainingBalance', label: 'Remaining balance', kind: 'money', computed: true, hint: 'Calculated from repayments. Shown on the payslip.' },
      { key: 'notes', label: 'Notes', kind: 'textarea' },
    ],
  },

  school_loan: {
    type: 'school_loan',
    label: 'School Loan',
    group: 'loan',
    icon: 'Landmark',
    blurb: 'Loan or salary advance recovered monthly.',
    amountKey: 'monthlyRepayment',
    balanceKey: 'remainingBalance',
    showBalanceOnPayslip: true,
    fields: [
      { key: 'loanAmount', label: 'Loan amount', kind: 'money', required: true },
      {
        key: 'interestRatePct',
        label: 'Simple interest rate (%)',
        kind: 'number',
        default: 0,
        hint: 'Added once to the total at loan start. 0 for an advance.',
      },
      { key: 'loanDate', label: 'Loan date', kind: 'date' },
      { key: 'reason', label: 'Reason / purpose', kind: 'text' },
      {
        key: 'repaymentType',
        label: 'Repayment type',
        kind: 'select',
        options: ['Fixed monthly amount', 'Variable per month'],
        default: 'Fixed monthly amount',
      },
      { key: 'monthlyRepayment', label: 'Monthly repayment', kind: 'money', hint: 'Set this and the instalments fill in — or set the instalments and this fills in.' },
      { key: 'instalments', label: 'Number of instalments', kind: 'number', hint: 'Auto-fills from the monthly amount, or enter it to set the monthly amount.' },
      { key: 'firstRepaymentMonth', label: 'First repayment month', kind: 'month' },
      { key: 'lastRepaymentMonth', label: 'Last repayment month', kind: 'text', computed: true, hint: 'Calculated from the first month and the instalments.' },
      {
        key: 'remainingBalance',
        label: 'Remaining balance',
        kind: 'money',
        computed: true,
        hint: 'Calculated from repayments. Shown on the payslip.',
      },
      { key: 'notes', label: 'Notes', kind: 'textarea' },
    ],
  },

  bank_scheme: {
    type: 'bank_scheme',
    label: 'Bank Loan',
    group: 'loan',
    icon: 'Building2',
    blurb: 'A monthly deduction paid over to a bank. Runs until it is stopped.',
    amountKey: 'monthlyDeduction',
    /* NO balanceKey, and that is the point.
     *
     * The school is never told what a staff member borrowed or over how many
     * months — the bank deals with the borrower and simply tells the school
     * what to deduct. Every one of the 39 records that existed when this was
     * rewritten had `remainingBalance` set to exactly the monthly figure,
     * because there was nothing else to put there. The payslip then printed
     * "Remaining: P3,880.80" against a deduction of P3,880.80, which any
     * reasonable person reads as "one payment to go".
     *
     * So there is no balance here at all. The deduction recurs at whatever
     * amount is in force and stops when somebody stops it, which is exactly
     * what the school actually knows. When a staff member tops up at the bank
     * and comes back with a different instalment, the amount changes from that
     * month (values.instalmentChanges) — it is not a new loan.
     */
    showBalanceOnPayslip: false,
    fields: [
      {
        key: 'bankName',
        label: 'Bank',
        kind: 'select',
        options: ['Stanbic', 'Bank of Baroda', 'BBS Bank', 'Other'],
        editableOptions: true,
        required: true,
      },
      { key: 'reference', label: 'Reference / loan account number', kind: 'text' },
      {
        key: 'monthlyDeduction',
        label: 'Monthly deduction',
        kind: 'money',
        required: true,
        hint: 'What the bank has asked the school to deduct. Change it whenever the bank does — the new figure applies from that month.',
      },
      ...DATE_FIELDS_TAIL,
    ],
  },

  funeral_cover: {
    type: 'funeral_cover',
    label: 'Funeral Cover',
    group: 'benefit',
    icon: 'Flower2',
    blurb: 'Funeral policy premium. Fully staff-funded (no subsidy).',
    amountKey: 'monthlyPremium',
    fields: [
      {
        key: 'provider',
        label: 'Provider',
        kind: 'select',
        options: ['Botswana Life', 'BIHL', 'Liberty', 'Old Mutual', 'Other'],
        editableOptions: true,
      },
      { key: 'policyNumber', label: 'Policy / scheme number', kind: 'text' },
      { key: 'planName', label: 'Plan name', kind: 'text' },
      { key: 'coveredMembers', label: 'Covered members', kind: 'text' },
      { key: 'monthlyPremium', label: 'Monthly premium', kind: 'money', required: true },
      ...DATE_FIELDS_TAIL,
    ],
  },

  car_insurance: {
    type: 'car_insurance',
    label: 'Car Insurance',
    group: 'benefit',
    icon: 'Car',
    blurb: 'Vehicle insurance premium recovered monthly.',
    amountKey: 'monthlyPremium',
    fields: [
      {
        key: 'provider',
        label: 'Provider',
        kind: 'select',
        options: ['BIC', 'Hollard', 'Alexander Forbes', 'Old Mutual Insure', 'Other'],
        editableOptions: true,
      },
      { key: 'policyNumber', label: 'Policy number', kind: 'text' },
      { key: 'vehicleDetails', label: 'Vehicle details', kind: 'text' },
      {
        key: 'coverType',
        label: 'Cover type',
        kind: 'select',
        options: ['Comprehensive', 'Third Party', 'Third Party + Theft'],
      },
      { key: 'monthlyPremium', label: 'Monthly premium', kind: 'money', required: true },
      ...DATE_FIELDS_TAIL,
    ],
  },

  rent: {
    type: 'rent',
    label: 'Rent',
    group: 'housing',
    icon: 'Home',
    blurb: 'School-accommodation rent. Fixed monthly amount.',
    amountKey: 'monthlyAmount',
    fields: [
      { key: 'monthlyAmount', label: 'Monthly amount', kind: 'money', required: true },
      ...DATE_FIELDS_TAIL,
    ],
  },

  electricity: {
    type: 'electricity',
    label: 'Electricity',
    group: 'utility',
    icon: 'Zap',
    blurb: 'Electricity usage. Bursar updates the amount each pay run.',
    amountKey: 'monthlyAmount',
    fields: [
      {
        key: 'monthlyAmount',
        label: 'Monthly amount',
        kind: 'money',
        required: true,
        hint: 'Update each pay run to reflect actual usage.',
      },
      ...DATE_FIELDS_TAIL,
    ],
  },

  water: {
    type: 'water',
    label: 'Water',
    group: 'utility',
    icon: 'Droplets',
    blurb: 'Water usage. Bursar updates the amount each pay run.',
    amountKey: 'monthlyAmount',
    fields: [
      {
        key: 'monthlyAmount',
        label: 'Monthly amount',
        kind: 'money',
        required: true,
        hint: 'Update each pay run to reflect actual usage.',
      },
      ...DATE_FIELDS_TAIL,
    ],
  },

  alarms: {
    type: 'alarms',
    label: 'Alarms / Security',
    group: 'benefit',
    icon: 'ShieldAlert',
    blurb: 'Home security monitoring. Fixed monthly amount.',
    amountKey: 'monthlyAmount',
    fields: [
      {
        key: 'provider',
        label: 'Provider',
        kind: 'select',
        options: ['G4S', 'ADT', 'Securitas', 'Other'],
        editableOptions: true,
      },
      { key: 'reference', label: 'Account / reference number', kind: 'text' },
      { key: 'monthlyAmount', label: 'Monthly amount', kind: 'money', required: true },
      ...DATE_FIELDS_TAIL,
    ],
  },

  school_fees: {
    type: 'school_fees',
    label: 'School Fees & Exams',
    group: 'recurring' as DeductionGroup,
    icon: 'GraduationCap',
    blurb: "Staff children's fees, combined per staff member (not per child).",
    amountKey: 'monthlyAmount',
    balanceKey: 'remainingBalance',
    showBalanceOnPayslip: true,
    fields: [
      {
        key: 'childrenCovered',
        label: 'Children covered',
        kind: 'text',
        hint: 'e.g. "Karabo Form 3, Lerato Year 8".',
      },
      { key: 'term', label: 'Term / period', kind: 'text' },
      { key: 'totalFees', label: 'Total fees', kind: 'money', required: true },
      { key: 'monthlyAmount', label: 'Monthly deduction', kind: 'money', required: true },
      { key: 'instalments', label: 'Number of instalments', kind: 'number', hint: 'Set the monthly amount and this fills in — or set this and the monthly amount fills in.' },
      {
        key: 'instalmentsCompleted',
        label: 'Instalments completed',
        kind: 'number',
        default: 0,
        computed: true,
      },
      {
        key: 'remainingBalance',
        label: 'Remaining balance',
        kind: 'money',
        computed: true,
        hint: 'Shown on the payslip.',
      },
      ...DATE_FIELDS_TAIL,
    ],
  },

  residence_permit: {
    type: 'residence_permit',
    label: 'Residence Permit',
    group: 'recurring' as DeductionGroup,
    icon: 'StampIcon',
    blurb: 'Recovery of a permit the school paid for upfront.',
    amountKey: 'monthlyAmount',
    balanceKey: 'remainingBalance',
    showBalanceOnPayslip: true,
    fields: [
      {
        key: 'permitType',
        label: 'Permit type',
        kind: 'select',
        options: ['Work Permit', 'Residence Permit', 'PR'],
      },
      { key: 'totalCost', label: 'Total permit cost', kind: 'money', required: true },
      { key: 'monthlyAmount', label: 'Monthly deduction', kind: 'money', required: true },
      { key: 'instalments', label: 'Number of instalments', kind: 'number', hint: 'Set the monthly amount and this fills in — or set this and the monthly amount fills in.' },
      {
        key: 'instalmentsCompleted',
        label: 'Instalments completed',
        kind: 'number',
        default: 0,
        computed: true,
      },
      {
        key: 'remainingBalance',
        label: 'Remaining balance',
        kind: 'money',
        computed: true,
        hint: 'Shown on the payslip.',
      },
      { key: 'notes', label: 'Notes', kind: 'textarea' },
    ],
  },

  paye: {
    type: 'paye',
    label: 'PAYE',
    group: 'statutory',
    icon: 'Receipt',
    blurb: 'Income tax, computed automatically from the active tax table.',
    // No fixed amount: the engine computes PAYE each pay run. The
    // bursar may override on a single payslip (audited).
    amountKey: null,
    fields: [
      {
        key: 'note',
        label: 'Note',
        kind: 'textarea',
        hint: 'PAYE is calculated automatically. Add this deduction only to record a manual note; the amount comes from the engine.',
      },
    ],
  },

  other: {
    type: 'other',
    label: 'Other / Ad-hoc',
    group: 'other',
    icon: 'CircleEllipsis',
    blurb: "Catch-all for one-off deductions that fit no other category.",
    amountKey: 'amount',
    fields: [
      { key: 'name', label: 'Deduction name', kind: 'text', required: true },
      { key: 'amount', label: 'Amount', kind: 'money', required: true },
      { key: 'instalments', label: 'Instalments (optional)', kind: 'number' },
      { key: 'notes', label: 'Notes', kind: 'textarea' },
    ],
  },
};

/** Stable order for the "add deduction" picker and profile cards. */
export const DEDUCTION_TYPE_ORDER: DeductionType[] = [
  'medical_aid',
  'funeral_cover',
  'car_insurance',
  'alarms',
  'salary_advance',
  'school_loan',
  'bank_scheme',
  'school_fees',
  'residence_permit',
  'rent',
  'electricity',
  'water',
  'other',
  'paye',
];

export type DeductionStatus = 'active' | 'paid_off' | 'archived' | 'suspended';

/**
 * One deduction instance attached to a staff member. Type-specific
 * fields live in `values`, keyed exactly as DEDUCTION_TYPES[type].fields.
 * The few money-affecting fields are surfaced as typed getters via the
 * pure helpers below so the engine never has to guess.
 */
export interface HrDeduction {
  id: string;
  staffId: string;
  type: DeductionType;
  /** Snapshot of the human label at creation (clean canonical name). */
  label: string;
  status: DeductionStatus;
  /** Type-specific form values. */
  values: Record<string, FieldValue>;
  /** Bursar-added ad-hoc fields (the "custom fields" section). */
  customFields: { key: string; value: string }[];
  createdAt: string;
  updatedAt: string;
  /** If set, this deduction applies to ONE payslip period only (e.g. a
   *  one-off unpaid-leave recovery). Payslip assembly skips it in any
   *  other month, so it never silently recurs. */
  oneOffPeriod?: { year: number; month: number };
  /** If this deduction was generated from a leave request (e.g. an
   *  unpaid-leave charge), the id of that leave — so editing or
   *  cancelling the leave can find and recompute/remove this charge. */
  sourceLeaveId?: string;
}

/* ------------------------------------------------------------------
   Statutory daily-rate helpers (Botswana Employment Act)
   ------------------------------------------------------------------
   A day's basic pay = monthly basic / working days per month. The
   divisor depends on the work-week: 5 days/wk = 22, 5.5 = 24, 6 = 26.
   Used for unpaid-leave recovery and severance.
   ------------------------------------------------------------------ */
export const DEFAULT_DAYS_PER_MONTH = 22;

export function dailyRate(basicMonthly: number, daysPerMonth: number = DEFAULT_DAYS_PER_MONTH): number {
  const d = daysPerMonth > 0 ? daysPerMonth : DEFAULT_DAYS_PER_MONTH;
  return round2((Number(basicMonthly) || 0) / d);
}

/** Unpaid-leave recovery = day's basic pay × unpaid days. */
export function unpaidLeaveDeduction(
  basicMonthly: number,
  unpaidDays: number,
  daysPerMonth: number = DEFAULT_DAYS_PER_MONTH,
): number {
  return round2(dailyRate(basicMonthly, daysPerMonth) * Math.max(0, Number(unpaidDays) || 0));
}

/**
 * Statutory severance benefit (Botswana Employment Act §27, 76–78):
 * 1 day's basic pay per completed month for the first 60 months, then
 * 2 days' per completed month beyond 60. Returns the gross amount and
 * the day count; eligibility (60 months / not on gratuity-or-pension)
 * is a policy decision left to the caller to surface.
 */
export interface SeveranceResult {
  monthsService: number;
  daysAccrued: number;
  dailyRate: number;
  amount: number;
  meetsStatutoryMinimum: boolean; // 60+ months continuous service
}
export function computeSeverance(
  basicMonthly: number,
  monthsService: number,
  daysPerMonth: number = DEFAULT_DAYS_PER_MONTH,
): SeveranceResult {
  const m = Math.max(0, Math.floor(monthsService));
  const firstTier = Math.min(m, 60) * 1;
  const secondTier = Math.max(0, m - 60) * 2;
  const daysAccrued = firstTier + secondTier;
  const rate = dailyRate(basicMonthly, daysPerMonth);
  return {
    monthsService: m,
    daysAccrued,
    dailyRate: rate,
    amount: round2(rate * daysAccrued),
    meetsStatutoryMinimum: m >= 60,
  };
}

/* ------------------------------------------------------------------
   Deduction pure helpers
   ------------------------------------------------------------------ */

function num(v: FieldValue | undefined): number {
  const n = typeof v === 'number' ? v : parseFloat(String(v ?? ''));
  return Number.isFinite(n) ? n : 0;
}

/** The monthly amount this deduction takes off a payslip this run.
 *  PAYE returns 0 here (it comes from the engine). Inactive/archived
 *  deductions return 0. */
export function deductionMonthlyAmount(d: HrDeduction): number {
  if (d.status === 'archived' || d.status === 'paid_off' || d.status === 'suspended') {
    return 0;
  }
  const cfg = DEDUCTION_TYPES[d.type];
  if (!cfg.amountKey) return 0;
  return round2(num(d.values[cfg.amountKey]));
}

/**
 * Display label for a deduction line on the payslip. Bank-scheme
 * deductions name the specific bank (from values.bankName) so the line
 * reads e.g. "Bank Scheme — Stanbic" instead of a generic "Bank Scheme".
 */
export function payslipDeductionLabel(d: HrDeduction): string {
  /* "Other / Ad-hoc" says nothing to the person paid. The form requires a
     name ("Pork", "Meat Sales", "Cafeteria") and that is what the payslip
     prints — 47 October 2026 lines read "Other / Ad-hoc" and staff took
     them for errors. */
  if (d.type === 'other') {
    const name = String(d.values?.name ?? '').trim();
    if (name) return name;
  }
  if (d.type === 'bank_scheme') {
    const bank = String(d.values?.bankName ?? '').trim();
    if (bank && !d.label.toLowerCase().includes(bank.toLowerCase())) {
      return `${d.label} — ${bank}`;
    }
  }
  return d.label;
}

/** The remaining balance to show on the payslip, or null if N/A. */
export function deductionRemainingBalance(d: HrDeduction): number | null {
  const cfg = DEDUCTION_TYPES[d.type];
  if (!cfg.balanceKey || !cfg.showBalanceOnPayslip) return null;
  return round2(num(d.values[cfg.balanceKey]));
}

/** True when a balance-carrying deduction has reached zero (badge cue). */
export function deductionIsCleared(d: HrDeduction): boolean {
  const bal = deductionRemainingBalance(d);
  return bal !== null && bal <= 0;
}

/** Total a loan/scheme owes at start, including simple interest once. */
export function loanTotalWithInterest(principal: number, interestRatePct: number): number {
  return round2(principal * (1 + (interestRatePct || 0) / 100));
}

/** Whole-number instalments implied by a total and a monthly amount. */
export function impliedInstalments(total: number, monthly: number): number {
  if (monthly <= 0) return 0;
  return Math.ceil(round2(total) / round2(monthly));
}

export interface DeductionPeriodResult {
  /** Amount to deduct THIS period (final payment capped to the balance). */
  amount: number;
  /** Balance after this period, or null for open-ended deductions. */
  remainingAfter: number | null;
  /** True once the loan is fully repaid. */
  cleared: boolean;
}

/** Parse a YYYY-MM (or YYYY-MM-DD) string to {year, month} WITHOUT using
 *  new Date(), which shifts by timezone. Returns null if not parseable. */
export function parseYearMonth(s: unknown): { year: number; month: number } | null {
  const m = /^(\d{4})-(\d{2})/.exec(String(s ?? ''));
  if (!m) return null;
  const year = Number(m[1]);
  const month = Number(m[2]);
  if (!year || month < 1 || month > 12) return null;
  return { year, month };
}

/** The current calendar month as {year, month}. */
export function currentYearMonth(): { year: number; month: number } {
  const n = new Date();
  return { year: n.getFullYear(), month: n.getMonth() + 1 };
}



const nextMonth = (y: number, m: number): { year: number; month: number } =>
  (m >= 12 ? { year: y + 1, month: 1 } : { year: y, month: m + 1 });

/** Has the pay run for this month already been produced? */
export function payRunCompleted(
  period: { year: number; month: number },
  completedRuns: Iterable<string>,
): boolean {
  return new Set([...completedRuns]).has(periodKey(period));
}

/**
 * The first month a deduction entered today can still reach.
 *
 * THE PAY RUN DECIDES, NOT THE CALENDAR. The old rule moved anything keyed
 * in after a fixed day of the month — the 20th — into the next month, on the
 * assumption that payroll was already under way. It often was not. A loan
 * entered on the 24th for a run that had not happened yet was pushed a month
 * out for no reason, and the school's answer to "why was this not deducted"
 * was a date nobody had chosen.
 *
 * So the question asked here is the real one: have this month's payslips
 * been produced? If not, the deduction belongs in this month. Walks forward
 * only over months whose run is genuinely finished.
 */
export function firstOpenPayRunMonth(
  completedRuns: Iterable<string>,
  now: Date = new Date(),
): { year: number; month: number } {
  const done = new Set([...completedRuns]);
  let year = now.getFullYear();
  let month = now.getMonth() + 1;
  /* Bounded so a mis-seeded set of "completed" months cannot spin. Two years
     of finished runs ahead of today is not a state worth walking past. */
  for (let guard = 0; guard < 24; guard += 1) {
    if (!done.has(periodKey({ year, month }))) return { year, month };
    ({ year, month } = nextMonth(year, month));
  }
  return { year, month };
}

/** The 'YYYY-MM' a new record should default its first month to: the first
 *  month whose pay run has not been done. What the office sees in the form,
 *  so the month shown is the month that will actually be charged. */
export function suggestedFirstMonthStr(
  completedRuns: Iterable<string>,
  now: Date = new Date(),
): string {
  const ym = firstOpenPayRunMonth(completedRuns, now);
  return `${ym.year}-${String(ym.month).padStart(2, '0')}`;
}

/** The months a human TYPED IN, as opposed to the ones inferred from when a
 *  record happened to be keyed. firstMonth and firstRepaymentMonth are the
 *  office answering "when does this start repaying"; startDate is the same
 *  answer in an older shape. loanDate (when the money was lent) and createdAt
 *  (when somebody got round to entering it) are NOT instructions about
 *  repayment, and are left out on purpose. */
function statedStartMonth(d: HrDeduction): { year: number; month: number } | null {
  const v = d.values || {};
  return parseYearMonth(v.firstMonth)
    ?? parseYearMonth(v.firstRepaymentMonth)
    ?? parseYearMonth(v.startDate);
}

/**
 * Loan start anchor: the month the record says it starts repaying, else the
 * month the money was lent, else the month it was keyed in.
 *
 * NOTHING MOVES IT. There used to be a floor here — a per-month cut-off day,
 * and anything entered after it was silently pushed to the following month.
 * It was meant to stop a deduction landing in a payroll that had already
 * been prepared, but it could not tell a prepared payroll from an unprepared
 * one; it only knew the date. On 25 Sep 2026 that cost one run P15,000:
 * Kenneth Adu Awuku's loan and Rajvinder Dhaliwal's advance were entered on
 * the 24th, four days past a cut-off of the 20th, both deliberately set to
 * start in September, and both were moved to October without a word. The
 * payslips came out light and the staff found it before the office did.
 *
 * The question the cut-off was guessing at is now asked directly, and at the
 * point where it can still be acted on: the entry form defaults the first
 * month to the first month whose pay run has NOT been produced
 * (firstOpenPayRunMonth), and makes whoever is saving confirm if they pick a
 * later one. By the time a record reaches here, its first month is a
 * decision somebody made, so it is honoured.
 */
export function deductionStartPeriod(d: HrDeduction): { year: number; month: number } {
  const chosen = statedStartMonth(d);
  if (chosen) return chosen;
  /* NOT loanDate. When nobody stated a repayment month, repayment began when
     the school set the deduction up — not when the money changed hands. They
     can be months apart: Evelyn Mpofu's P16,500 was lent on 1 May 2026 and
     entered on 22 June, and her four instalments ran June to September, which
     is exactly what her payslips show. Anchoring to the loan date would post
     May and June instalments that were never taken, declare the loan settled
     in August, and stop the September deduction that actually cleared it. */
  const raw = d.createdAt;
  const parsed = parseYearMonth(raw);
  if (parsed) return parsed;
  const dt = new Date(String(raw ?? ''));
  return Number.isNaN(dt.getTime())
    ? currentYearMonth()
    : { year: dt.getFullYear(), month: dt.getMonth() + 1 };
}

/** The balance a finite loan owed at its start anchor (0 = open-ended). */
export function deductionOpeningBalance(d: HrDeduction): number {
  const cfg = DEDUCTION_TYPES[d.type];
  if (!cfg.balanceKey) return 0;
  const v = d.values || {};
  const original = num(v.originalAmount);
  if (original > 0) return round2(original);
  const loan = num(v.loanAmount);
  if (loan > 0) return loanTotalWithInterest(loan, num(v.interestRatePct));
  return round2(num(v[cfg.balanceKey]));
}

/* ==================================================================
   VARYING INSTALMENTS AND TOP-UPS
   ------------------------------------------------------------------
   A loan's monthly amount is not a constant. A staff member can ask for
   their instalment to be lowered, raised or paused; they can go back to the
   lender and top up, which increases what is owed and usually changes the
   instalment too. The school needs the balance to stay right through all of
   that.

   The previous engine could not express any of it. It computed

       balanceBefore = opening - elapsed * monthly

   which assumes ONE monthly amount for the whole life of the loan. Change
   the amount half way through and every balance before and after is wrong,
   silently: nothing in the arithmetic notices.

   So the amount and the balance are now derived from two histories kept on
   the deduction:

     values.instalmentChanges  [{ fromMonth: 'YYYY-MM', amount }]
        The monthly amount in force FROM that month until the next change.
        `amount` may be 0 — a pause is a change like any other, not a special
        case, so a paused loan keeps its balance and simply pays nothing.

     values.topUps             [{ month: 'YYYY-MM', amount, note }]
        Added to what is owed at the START of that month. A top-up is never a
        second loan: the borrower has one debt with the lender, and two rows
        on a payslip for one debt is how a balance stops reconciling.

   Both are append-only records of what happened, so the balance for any
   month is a pure function of them. That matters more than it sounds: a
   payslip issued in March must still compute the same figure when it is
   re-rendered in November, and it will, because nothing here depends on when
   the question is asked.

   The walk is month by month rather than closed-form, which is a few dozen
   iterations at worst and is the only way to honour "pay the smaller of the
   instalment and what is left" at every step.
   ================================================================== */

export interface InstalmentChange { fromMonth: string; amount: number; note?: string; by?: string; at?: string }
export interface LoanTopUp { month: string; amount: number; note?: string; by?: string; at?: string }

function readChanges(d: HrDeduction): InstalmentChange[] {
  const raw = (d.values as Record<string, unknown>)?.instalmentChanges;
  if (!Array.isArray(raw)) return [];
  return (raw as InstalmentChange[])
    .map((c) => ({ fromMonth: String(c?.fromMonth ?? ''), amount: round2(num(c?.amount)) }))
    .filter((c) => parseYearMonth(c.fromMonth) !== null)
    .sort((a, b) => (a.fromMonth < b.fromMonth ? -1 : a.fromMonth > b.fromMonth ? 1 : 0));
}

function readTopUps(d: HrDeduction): LoanTopUp[] {
  const raw = (d.values as Record<string, unknown>)?.topUps;
  if (!Array.isArray(raw)) return [];
  return (raw as LoanTopUp[])
    .map((t) => ({ month: String(t?.month ?? ''), amount: round2(num(t?.amount)) }))
    .filter((t) => parseYearMonth(t.month) !== null && t.amount > 0)
    .sort((a, b) => (a.month < b.month ? -1 : a.month > b.month ? 1 : 0));
}

/** 'YYYY-MM' for a period, the key both histories are written against. */
function periodKey(p: { year: number; month: number }): string {
  return p.year + '-' + String(p.month).padStart(2, '0');
}

/**
 * The instalment in force for a given period: the latest change dated on or
 * before it, else the deduction's base monthly amount. Archived, paid-off and
 * suspended deductions take nothing regardless.
 */
export function instalmentForPeriod(d: HrDeduction, period: { year: number; month: number }): number {
  if (d.status === 'archived' || d.status === 'paid_off' || d.status === 'suspended') return 0;
  const key = periodKey(period);
  let amount: number | null = null;
  for (const c of readChanges(d)) {
    if (c.fromMonth <= key) amount = c.amount;
    else break;
  }
  return amount === null ? deductionMonthlyAmount(d) : round2(amount);
}

/** Total topped up on or before a period — for display beside the original. */
export function topUpsToDate(d: HrDeduction, period: { year: number; month: number }): number {
  const key = periodKey(period);
  return round2(readTopUps(d).filter((t) => t.month <= key).reduce((a, t) => a + t.amount, 0));
}

/**
 * Period-aware deduction.
 *
 * Open-ended deductions (medical aid, and now bank loans) recur at whatever
 * instalment is in force for that month — there is no balance to run down and
 * no end date; they stop when somebody stops them.
 *
 * Balance-carrying loans walk from their start month to the period asked
 * about, adding any top-up due at the start of each month and taking the
 * smaller of that month's instalment and what is left. Fully derived: same
 * inputs, same answer, whenever it is asked.
 */
export function deductionForPeriod(
  d: HrDeduction,
  period: { year: number; month: number },
): DeductionPeriodResult {
  const cfg = DEDUCTION_TYPES[d.type];
  const due = instalmentForPeriod(d, period);
  const opening = deductionOpeningBalance(d);

  if (!cfg.balanceKey || opening <= 0) {
    // Open-ended or unconfigured: recur at this month's instalment.
    return { amount: due, remainingAfter: cfg.balanceKey ? round2(opening) : null, cleared: false };
  }

  const startIdx = periodIndex(deductionStartPeriod(d));
  const askIdx = periodIndex(period);
  if (askIdx < startIdx) {
    // Repayment has not started yet — nothing due, nothing repaid.
    return { amount: 0, remainingAfter: round2(opening), cleared: false };
  }

  const topUps = readTopUps(d);
  let balance = opening;

  for (let idx = startIdx; idx <= askIdx; idx++) {
    const year = Math.floor(idx / 12);
    const month = (idx % 12) + 1;
    const key = periodKey({ year, month });

    // A top-up lands at the START of its month, so it is repayable from that
    // same month rather than the next one.
    for (const t of topUps) if (t.month === key) balance = round2(balance + t.amount);

    if (idx === askIdx) {
      if (balance <= 0) return { amount: 0, remainingAfter: 0, cleared: true };
      const amount = round2(Math.min(instalmentForPeriod(d, { year, month }), balance));
      const after = round2(balance - amount);
      return { amount, remainingAfter: after, cleared: after <= 0 };
    }

    if (balance <= 0) continue;
    balance = round2(Math.max(0, balance - Math.min(instalmentForPeriod(d, { year, month }), balance)));
  }

  // Unreachable: the loop always returns when idx === askIdx.
  return { amount: 0, remainingAfter: round2(balance), cleared: balance <= 0 };
}

/**
 * The LIVE outstanding balance of a balance-carrying deduction (loan /
 * advance) as at a period — defaults to the current month. Uses the SAME
 * engine as the payslip (deductionForPeriod), so the admin view and the
 * payslip always agree. NB: the stored `values.remainingBalance` is only a
 * seed and is NOT decremented as payroll runs, so it must never be shown
 * as a live balance. Returns null for open-ended deductions (no balance).
 */
export function deductionCurrentBalance(
  d: HrDeduction,
  period?: { year: number; month: number },
): number | null {
  const cfg = DEDUCTION_TYPES[d.type];
  if (!cfg.balanceKey || !cfg.showBalanceOnPayslip) return null;
  if (deductionOpeningBalance(d) <= 0) return null;
  const res = deductionForPeriod(d, period ?? currentYearMonth());
  return res.remainingAfter == null ? null : round2(res.remainingAfter);
}

/* ==================================================================
   TAX TABLES (versioned Botswana PAYE)
   ------------------------------------------------------------------
   Brackets store the rate as a PERCENT for human readability (this is
   how a bursar reads a BURS table). The engine divides by 100.
   `to: null` denotes the open-ended top bracket.
   ================================================================== */

export interface TaxBracket {
  /** Lower bound (inclusive), annual BWP. */
  from: number;
  /** Upper bound (exclusive), annual BWP. null = no upper bound. */
  to: number | null;
  /** Marginal rate on income in this band, as a percent (e.g. 12.5). */
  ratePct: number;
}

export interface HrTaxTable {
  /** Id is the effective-from date, e.g. "2025-07-01". */
  id: string;
  /** Human label, e.g. "FY ending 30 June 2026". */
  label: string;
  /** Sorted ascending by `from`. */
  brackets: TaxBracket[];
  /** Exactly one table is active at a time. */
  active: boolean;
  activeFrom: string; // ISO date
  archivedAt?: string; // ISO
  uploadedBy?: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Confirmed enacted resident PAYE table for the Botswana tax year
 * ending 30 June 2026 (BURS; per PwC Worldwide Tax Summaries, last
 * reviewed 9 January 2026). Top rate remains 25%; the mooted 26.5%
 * was a budget proposal not present in the enacted table — exactly
 * the kind of change the versioned-table design absorbs when/if it
 * lands. Seed value for Phase 1.
 */
export const FY2026_TAX_TABLE: Omit<
  HrTaxTable,
  'createdAt' | 'updatedAt'
> = {
  id: '2025-07-01',
  label: 'Botswana resident PAYE — FY ending 30 June 2026',
  active: true,
  activeFrom: '2025-07-01',
  brackets: [
    { from: 0, to: 48000, ratePct: 0 },
    { from: 48000, to: 84000, ratePct: 5 },
    { from: 84000, to: 120000, ratePct: 12.5 },
    { from: 120000, to: 156000, ratePct: 18.75 },
    { from: 156000, to: null, ratePct: 25 },
  ],
};

/* ==================================================================
   PAYE ENGINE (pure)
   ------------------------------------------------------------------
   Acepsle is responsible for the correctness of THIS engine given a
   valid table; LKC is responsible for keeping the active table
   current. Tested by construction against the BURS cumulative
   figures (P1,800 at 84k; P6,300 at 120k; P13,050 at 156k).
   ================================================================== */

/** Annual PAYE for a taxable annual income against a table. */
export function computeAnnualPAYE(annualTaxable: number, table: HrTaxTable): number {
  const income = Math.max(0, annualTaxable);
  let tax = 0;
  for (const b of table.brackets) {
    const upper = b.to ?? Infinity;
    if (income <= b.from) break;
    const inBand = Math.min(income, upper) - b.from;
    if (inBand > 0) tax += inBand * (b.ratePct / 100);
  }
  return round2(tax);
}

/** Monthly PAYE = annual tax on (monthly gross × 12), divided by 12.
 *  Phase 1: gross == taxable (no pre-tax deductions, decision #5). */
export function monthlyPAYE(monthlyGross: number, table: HrTaxTable): number {
  const annual = computeAnnualPAYE(round2(monthlyGross) * 12, table);
  return round2(annual / 12);
}

/** The marginal rate (percent) that applies at a given annual income. */
export function marginalRatePct(annualIncome: number, table: HrTaxTable): number {
  let rate = 0;
  for (const b of table.brackets) {
    if (annualIncome > b.from) rate = b.ratePct;
  }
  return rate;
}

export interface GratuityTaxInput {
  /** Gross gratuity amount being paid out. */
  grossGratuity: number;
  /** Whole/partial years the contract ran (e.g. 2). */
  contractYears: number;
  /** Staff member's current annual salary (basic × 12 baseline). */
  currentAnnualSalary: number;
  table: HrTaxTable;
}

export interface GratuityTaxResult {
  grossGratuity: number;
  exemptAmount: number; // the tax-free 50%
  taxableAmount: number; // the taxable 50%
  /** taxable / years, the slice added to annual salary. */
  annualisedSlice: number;
  marginalRatePct: number;
  tax: number;
  net: number;
}

/**
 * Gratuity tax by the spread-over-years marginal-rate method
 * (decision #9; Botswana practice — 50% of a gratuity under a
 * post-1999 contract is tax-free, the other 50% taxable).
 *
 *   exempt   = 50% of gratuity (tax-free)
 *   taxable  = the other 50%
 *   slice    = taxable / contractYears
 *   rate     = marginal rate at (currentAnnualSalary + slice)
 *   tax      = taxable × rate
 */
export function computeGratuityTax(input: GratuityTaxInput): GratuityTaxResult {
  const { grossGratuity, contractYears, currentAnnualSalary, table } = input;
  const gross = Math.max(0, round2(grossGratuity));
  const exempt = round2(gross * 0.5);
  const taxable = round2(gross - exempt);
  const years = contractYears > 0 ? contractYears : 1;
  const slice = round2(taxable / years);
  const rate = marginalRatePct(round2(currentAnnualSalary) + slice, table);
  const tax = round2(taxable * (rate / 100));
  return {
    grossGratuity: gross,
    exemptAmount: exempt,
    taxableAmount: taxable,
    annualisedSlice: slice,
    marginalRatePct: rate,
    tax,
    net: round2(gross - tax),
  };
}

/** Monthly gratuity accrual = (basic × rate%) per month worked. */
export function monthlyGratuityAccrual(basicSalary: number, gratuityRatePct: number): number {
  return round2(basicSalary * (gratuityRatePct / 100));
}

/** Absolute month index for ordering periods (year*12 + month-1). */
export function periodIndex(p: { year: number; month: number }): number {
  return p.year * 12 + (Math.max(1, p.month) - 1);
}

/**
 * Total basic salary paid in the CURRENT contract up to and including the
 * period being prepared — the gratuity base (gratuity resets per contract).
 *   = opening figure (basic paid before go-live this contract)
 *   + basic on every saved payslip since the contract start (and before
 *     the current period)
 *   + the basic for the period currently being prepared.
 * Derived, never a stored counter — so it can't drift.
 */
export function contractGratuityBasePaid(args: {
  openingBasicPaid?: number;
  contractStartISO?: string;
  savedPayslips: { period: { month: number; year: number }; basic?: number }[];
  currentPeriod: { month: number; year: number };
  currentBasic: number;
  /** Prior partial payouts this contract; their consumed base is netted out
   *  so only the unpaid basic balance remains available. */
  payouts?: { basePaidOut?: number }[];
}): number {
  const opening = Math.max(0, Number(args.openingBasicPaid) || 0);
  const startIdx = args.contractStartISO
    ? (() => {
        const d = new Date(args.contractStartISO);
        return Number.isNaN(d.getTime()) ? -Infinity : d.getFullYear() * 12 + d.getMonth();
      })()
    : -Infinity;
  const curIdx = periodIndex(args.currentPeriod);
  let sum = 0;
  for (const p of args.savedPayslips) {
    const idx = periodIndex(p.period);
    if (idx < startIdx || idx >= curIdx) continue; // current handled below
    sum += Number(p.basic) || 0;
  }
  const paidOut = (args.payouts ?? []).reduce((a, p) => a + (Number(p.basePaidOut) || 0), 0);
  return Math.max(0, round2(opening + sum + (Number(args.currentBasic) || 0) - paidOut));
}

/** Gratuity accrued on a given total basic-paid figure. */
export function gratuityAccruedOn(basePaid: number, gratuityRatePct: number): number {
  return round2((Number(basePaid) || 0) * ((Number(gratuityRatePct) || 0) / 100));
}

/**
 * Resolve which end-of-service benefit applies on exit. An explicit
 * per-contract setting always wins; for older contracts with nothing set,
 * fall back to the gratuity-eligibility flag (eligible → gratuity, otherwise
 * → severance) so legacy exits behave exactly as before.
 */
export function resolveExitBenefit(
  contractBenefit: 'gratuity' | 'severance' | 'none' | undefined,
  gratuityEligible: boolean,
): 'gratuity' | 'severance' | 'none' {
  if (contractBenefit) return contractBenefit;
  return gratuityEligible ? 'gratuity' : 'severance';
}

/* ==================================================================
   LEAVE ALLOCATIONS
   ------------------------------------------------------------------
   Adjustable per cohort and per staff member. Balances print on
   every payslip, and are always derived — there is no stored
   running total to drift.
   ================================================================== */

export const DEFAULT_LEAVE_ENTITLEMENTS: Record<string, number> = {
  /**
   * EIGHTEEN. It said 36 until 5 September 2026, and 36 was never a number
   * this school used: every cohort policy says 18 (20 for management and
   * teaching) and 65 of 67 per-staff allocations say 18.
   *
   * It went unnoticed because it was almost never reached — resolveEntitlements
   * falls through to these defaults only when neither a per-staff allocation
   * nor a cohort policy answers, and every 2026 category has a policy. The
   * year it would have surfaced is 2027, when the policies run out: without
   * this correction the whole school would have quietly doubled its annual
   * leave on 1 January, and the first person to notice would have been
   * somebody taking it.
   *
   * A default that is never exercised is a default nobody checks. This one
   * now matches what the school actually grants.
   */
  annual: 18,
  /**
   * TWENTY, DOWN FROM FORTY-TWO. Botswana's Employment and Labour Relations
   * Act 2025 came into force on 1 September 2026 and sets paid sick leave at
   * 20 days; the school has chosen to move to it rather than keep the 42 it
   * granted before.
   *
   * A statutory count is a FLOOR, so 42 was lawful and this is a policy
   * decision, not a compliance one — recorded here because a reduction in
   * what staff already had should never look like a typo. It is not as
   * severe as it reads: the 42 previously covered hospitalisation too, and
   * that is now a separate 20 below.
   */
  sick: 20,
  /**
   * HOSPITALISATION IS ITS OWN ENTITLEMENT, separate from ordinary sick
   * leave, created by the same Act at 20 days — so somebody hospitalised
   * does not spend the sick leave they will still need afterwards.
   */
  hospitalisation: 20,
};

/**
 * Where a cohort's entitlement differs from the school-wide default.
 *
 * TEACHERS USED TO BE CALCULATED ENTIRELY DIFFERENTLY. Their annual leave
 * was an accumulate-and-reset cycle tied to the April, August and December
 * holidays — the reasoning being that a teacher's leave IS the school
 * holiday — and it never touched an entitlement number at all. From 1
 * October 2026 the school abandoned that: teachers accrue 21 days monthly
 * and carry them, exactly like everybody else.
 *
 * This map exists because deleting that model without it would have quietly
 * handed every teacher the school-wide default.
 */
export const DEFAULT_ENTITLEMENTS_BY_CATEGORY: Record<string, Record<string, number>> = {
  teaching: { annual: 21 },
};

/**
 * When each cohort's seeded opening balances are "as of".
 *
 * Non-teaching balances were migrated as at 30 June 2026. Teachers are being
 * seeded separately, as at 30 September 2026, because their balances only
 * became meaningful when they moved onto the same engine — so their accrual
 * runs from 1 October and not before. Accruing a teacher from June would
 * credit them three months they were never on this model for.
 */
export const LEAVE_ACCRUAL_START_BY_CATEGORY: Record<string, string> = {
  teaching: '2026-09-30',
};

/**
 * Leave-accrual anchor: the date the migrated opening balances are "as of".
 * Annual leave accrues monthly from this date on top of the opening balance;
 * leave dated before it is assumed already netted into the opening, so it is
 * not subtracted again. Editable via HrPayrollSettings.leaveAccrualStart.
 */
export const DEFAULT_LEAVE_ACCRUAL_START = '2026-06-30';

/** Types whose balance accrues monthly and carries indefinitely (vs reset). */
export const ACCRUING_LEAVE_TYPES = ['annual'];

export interface HrLeaveAllocation {
  id: string; // == staffId
  staffId: string;
  /** Per-leave-type annual entitlement (days). */
  entitlements: Record<string, number>;
  /**
   * Per-leave-type opening balance captured at go-live (signed days):
   * positive = brought forward from a prior period, negative = already
   * taken this leave year before the system took over. Folded into the
   * derived remaining balance so day-one figures are correct.
   */
  openingBalances?: Record<string, number>;
  /** Tax/leave year this allocation applies to, e.g. "2026". */
  year: string;
  /** The brought-forward line of HR's 2026 paper leave card (teachers; seed/
   *  import-teacher-leave-cards.ts) — printed on the card (lib/leaveCard). */
  leaveCard2026?: { decBal25: number; holDays: number; sold: number; cf2026: number; firstMonth?: number };
  createdAt: string;
  updatedAt: string;
}

export interface LeaveBalance {
  type: string;
  entitlement: number;
  taken: number;
  /** Opening balance carried in at go-live (signed days); 0 if none. */
  opening?: number;
  /**
   * Days SOLD back to the school (leave encashment), already subtracted from
   * `remaining`. Kept apart from `taken` because they are not absence: a
   * teacher who sold five days was at work on all of them, and a calendar
   * or an attendance check that read them as leave would be wrong.
   *
   * Present only when something was sold, so every balance computed before
   * encashment existed is byte-for-byte unchanged.
   */
  sold?: number;
  remaining: number;
}

/* ==================================================================
   GRATUITY ACCRUAL / PAYOUT RECORDS
   ================================================================== */

export interface HrGratuityAccrual {
  id: string;
  staffId: string;
  contractId?: string;
  /** Period this accrual belongs to, e.g. "2026-05". */
  period: string;
  amount: number;
  payslipId?: string;
  createdAt: string;
  updatedAt: string;
}

export interface HrGratuityPayout {
  id: string;
  staffId: string;
  contractId?: string;
  payslipId: string;
  result: GratuityTaxResult;
  contractYears: number;
  /** The amount of gratuity BASE (basic) this payout consumed. The running
   *  base nets this out, so the unpaid basic balance carries forward to
   *  future payouts. Absent on legacy rows → derived from result if needed. */
  basePaidOut: number;
  createdAt: string;
  updatedAt: string;
}

/* ==================================================================
   PAYSLIP MODEL
   ------------------------------------------------------------------
   A payslip is an IMMUTABLE SNAPSHOT. Once finalised it never
   recalculates — not even when a new tax table is activated. It
   stamps the tax-table id it used.
   ================================================================== */

export type PayslipLineKind = 'earning' | 'deduction' | 'info';

export interface PayslipLine {
  /** Clean canonical code, e.g. "BASIC", "ALLOW_HOUSING", "PAYE". */
  code: string;
  label: string;
  kind: PayslipLineKind;
  amount: number;
  /** For balance-carrying deductions: remaining balance to print. */
  remainingBalance?: number;
}

export interface PayslipPeriod {
  /** 1–12. */
  month: number;
  year: number;
  /** ISO dates covering the period (for the "Date From / To" header). */
  from: string;
  to: string;
}

export type PayslipStatus = 'draft' | 'finalised';

/** "Sent" means the school's mail server accepted it, not that it was read. */
export interface PayslipSendStatus {
  email: 'sent' | 'failed';
  lastAttemptAt: string;
  lastError?: string;
}

export interface HrPayslip {
  id: string;
  staffId: string;
  /** Snapshot of identity so the slip reads correctly years later. */
  staffName: string;
  employeeNo: string;
  jobTitle: string;
  department?: string;
  period: PayslipPeriod;
  /** Reference shown on the slip, e.g. "SLIP/1629". */
  reference: string;
  status: PayslipStatus;

  lines: PayslipLine[];
  earningsTotal: number; // gross
  /** Basic salary paid this period — summed across a contract to derive
   *  the gratuity base (gratuity = rate × total basic paid this contract). */
  basic?: number;
  deductionsTotal: number;
  paye: number;
  net: number;
  netInWords: string;

  /** Tax-table version that produced PAYE on this slip. */
  taxTableId: string;
  taxTableLabel: string;

  /** Leave balances at the time of the run. */
  leaveBalances: LeaveBalance[];

  /** Gratuity payout, present only on the last payslip of a contract. */
  gratuity?: GratuityTaxResult;

  bank?: BankAccountDetails;
  /** Storage path / URL of the generated PDF. */
  pdfUrl?: string;
  emailedAt?: string;
  /** Last email attempt, written by the sendPayslipEmail Cloud Function
   *  (merge). Carried across a regenerate so "who received theirs" is not
   *  wiped by re-rendering the PDF. */
  send?: PayslipSendStatus;
  finalisedAt?: string;
  finalisedBy?: string;
  createdAt: string;
  updatedAt: string;
}

/* ==================================================================
   PAYSLIP ASSEMBLY (pure)
   ------------------------------------------------------------------
   The single source of truth for what a payslip's numbers are.
   GUARANTEE: net === gross - totalDeductions, exactly, to the thebe.
   (The school's old Odoo sample had 4000 gross / 4000 ded / 500 net /
    "three thousand five hundred" in words — irreconcilable. We never
    ship a slip whose lines don't add up.)
   ================================================================== */

export interface PayslipTotals {
  earningsTotal: number;
  deductionsTotal: number;
  paye: number;
  net: number;
}

/** Compute and verify the totals from a set of lines. PAYE is one of
 *  the deduction lines (code "PAYE"); we surface it separately too. */
export function assemblePayslipTotals(lines: PayslipLine[]): PayslipTotals {
  const earningsTotal = round2(
    lines.filter((l) => l.kind === 'earning').reduce((a, l) => a + l.amount, 0),
  );
  const deductionLines = lines.filter((l) => l.kind === 'deduction');
  const deductionsTotal = round2(deductionLines.reduce((a, l) => a + l.amount, 0));
  const paye = round2(
    deductionLines.filter((l) => l.code === 'PAYE').reduce((a, l) => a + l.amount, 0),
  );
  const net = round2(earningsTotal - deductionsTotal);
  return { earningsTotal, deductionsTotal, paye, net };
}

/**
 * Convert a BWP amount to words for the "Net in Words" line, e.g.
 * 3500 → "Three Thousand, Five Hundred Pula". Handles thebe as
 * "and NN Thebe" when present.
 */
export function pulaInWords(amount: number): string {
  const rounded = round2(amount);
  const pula = Math.floor(rounded);
  const thebe = Math.round((rounded - pula) * 100);

  const words = numberToWords(pula);
  const pulaPart = `${words} Pula`;
  if (thebe > 0) return `${pulaPart} and ${numberToWords(thebe)} Thebe`;
  return pulaPart;
}

const ONES = [
  'Zero', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight',
  'Nine', 'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen',
  'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen',
];
const TENS = [
  '', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy',
  'Eighty', 'Ninety',
];

function threeDigitWords(n: number): string {
  let out = '';
  const hundreds = Math.floor(n / 100);
  const rest = n % 100;
  if (hundreds) out += `${ONES[hundreds]} Hundred`;
  if (rest) {
    if (out) out += ' ';
    if (rest < 20) out += ONES[rest];
    else {
      out += TENS[Math.floor(rest / 10)];
      if (rest % 10) out += `-${ONES[rest % 10]}`;
    }
  }
  return out;
}

function numberToWords(n: number): string {
  if (n === 0) return 'Zero';
  const scales = ['', ' Thousand', ' Million', ' Billion'];
  const groups: number[] = [];
  let rem = n;
  while (rem > 0) {
    groups.push(rem % 1000);
    rem = Math.floor(rem / 1000);
  }
  const parts: string[] = [];
  for (let i = groups.length - 1; i >= 0; i--) {
    if (groups[i] === 0) continue;
    parts.push(`${threeDigitWords(groups[i])}${scales[i]}`);
  }
  return parts.join(', ');
}

/* ==================================================================
   SETTINGS SINGLETON
   ================================================================== */

export interface HrPayrollSettings {
  id: string; // == PAYROLL_SETTINGS_ID
  /** Default medical aid provider (decision #10: locked to BOMaid,
   *  editable here at school level). */
  medicalAidProvider: string;
  /** Employer share of medical aid premium, as a percent (default 50). */
  medicalAidEmployerPct: number;
  /** Bursar-extended dropdown option lists, keyed by FieldDef.key. */
  dropdowns: Record<string, string[]>;
  /** Payslip email sender (reuses the academic SMTP setup). */
  payslipFromAddress: string;
  /** The disclaimer footer printed on every payslip. */
  payslipDisclaimer: string;
  /** BURS employer name printed on statutory returns (defaults to SCHOOL.name). */
  bursEmployerName?: string;
  /** BURS employer Tax Identification Number (TIN) for ITW-7/8/10. */
  bursEmployerTin?: string;
  /** BURS employer address lines (defaults to SCHOOL.contact.addressLines). */
  bursEmployerAddress?: string[];
  /** Date the migrated leave opening balances are "as of"; annual leave
   *  accrues monthly from here. Falls back to DEFAULT_LEAVE_ACCRUAL_START. */
  leaveAccrualStart?: string;
  /**
   * First pay month (YYYY-MM) whose payslips may show a SICK leave balance.
   *
   * The school has accurate annual-leave balances carried over from its own
   * records, but not sick. Printing a sick balance nobody can vouch for is
   * worse than printing none: a staff member reads it as their entitlement
   * and plans against it, and the school then has to argue them out of a
   * figure its own payslip gave them.
   *
   * ABSENT MEANS HIDDEN. Sick balances stay off every payslip until this is
   * set, which is the safe default when the honest answer is "we do not know
   * yet". Annual is unaffected. Set it with seed/set-sick-balances-from.cjs
   * once the real figures are in.
   */
  sickBalancesFromMonth?: string;
  createdAt: string;
  updatedAt: string;
}

export const DEFAULT_PAYROLL_SETTINGS: Omit<
  HrPayrollSettings,
  'createdAt' | 'updatedAt'
> = {
  id: PAYROLL_SETTINGS_ID,
  medicalAidProvider: 'BOMaid',
  medicalAidEmployerPct: 50,
  dropdowns: {},
  payslipFromAddress: 'reports@lkc.ac.bw',
  payslipDisclaimer:
    'PAYE calculated using Botswana tax tables effective {DATE}. The School is responsible for verifying the calculation against current BURS regulations. Acepsle Pty Ltd makes no representation as to the accuracy of statutory deductions.',
  leaveAccrualStart: DEFAULT_LEAVE_ACCRUAL_START,
};

/* ==================================================================
   AUDIT LOG
   ================================================================== */

export interface HrAuditEntry {
  id: string;
  /** e.g. "payslip.finalise", "taxTable.activate", "deduction.edit". */
  action: string;
  actorId: string;
  actorEmail?: string;
  /** Subject reference, e.g. a staffId / payslipId / taxTableId. */
  subjectId?: string;
  before?: unknown;
  after?: unknown;
  at: string; // ISO
}

/* ==================================================================
   LABELS (display)
   ================================================================== */

export const DEDUCTION_TYPE_LABELS: Record<DeductionType, string> =
  Object.fromEntries(
    (Object.keys(DEDUCTION_TYPES) as DeductionType[]).map((t) => [
      t,
      DEDUCTION_TYPES[t].label,
    ]),
  ) as Record<DeductionType, string>;

export const MONTH_LABELS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/** A clean canonical payslip code for an allowance line. */
export function allowanceCode(label: string): string {
  return `ALLOW_${label.toUpperCase().replace(/[^A-Z0-9]+/g, '_').replace(/^_|_$/g, '')}`;
}

/** A clean canonical payslip code for a deduction instance. */
export function deductionCode(type: DeductionType): string {
  return type.toUpperCase();
}

/* ==================================================================
 * LOAN REVIEW — READ THIS BEFORE THE PAYSLIPS ARE GENERATED
 * ------------------------------------------------------------------
 * In August 2026 ten members of staff stopped having a school loan
 * deducted and nobody noticed until a parent-facing question in September
 * made somebody compare two months by hand. Three of the ten still owed
 * P6,409.05 between them and had no monthly repayment recorded; one was
 * repaying a P19,999 balance at P2.00 a month, which clears it in roughly
 * eight hundred years.
 *
 * NONE OF THAT WAS AN ERROR THE SYSTEM COULD SEE. deductionForPeriod does
 * exactly what it is told: no repayment amount means an instalment of zero,
 * a zero line is dropped from the payslip, and P2.00 against P19,999 is
 * arithmetically valid. The loan simply stops being collected, in silence,
 * and a finalised payslip is a hard thing to take back.
 *
 * So the check is not "is the arithmetic right" — it always is. It is "does
 * this arrangement actually repay the loan", asked of every balance-carrying
 * deduction BEFORE the run, while the answer is still cheap to act on.
 * ================================================================== */

/** Slower than this and the arrangement is not really a repayment plan.
 *  Three years: longer than any loan the school has written. */
export const LOAN_SLOW_MONTHS = 36;

export type LoanConcernKind = 'no-instalment' | 'very-slow' | 'starts-later';

/** 'action' needs somebody to do something; 'note' is the run behaving
 *  correctly in a way the office would otherwise query. */
export type LoanConcernSeverity = 'action' | 'note';

export interface LoanConcern {
  deductionId: string;
  staffId: string;
  /** The staff member's name when the caller could resolve one. */
  staffName?: string;
  type: DeductionType;
  label: string;
  balance: number;
  /** The repayment the record is CONFIGURED for, whether or not this run
   *  takes it. Zero means none is set. */
  instalment: number;
  /** Whole months to clear at that rate; null when it never would. */
  monthsToClear: number | null;
  kind: LoanConcernKind;
  severity: LoanConcernSeverity;
  /** Written for the office, not for a developer. */
  message: string;
}

/**
 * Every balance-carrying deduction that will not repay itself.
 *
 * Judged on `balanceKey`, not on a list of type names: that is the same test
 * `deductionForPeriod` uses to decide a deduction is finite, so the review
 * covers exactly the deductions the engine treats as loans and cannot fall
 * out of step with them when a new loan type is added.
 *
 * THE ARRANGEMENT, NOT THIS MONTH'S OUTCOME. A loan set up after the cutoff
 * month takes nothing this run, which is correct and not a fault — but if its
 * instalment is also wrong, saying only "starts next month" would hide the
 * thing that actually needs fixing. So the repayment is judged as configured,
 * and a later start is reported separately as a note.
 *
 * A cleared balance is never a concern — it is a loan that finished, which is
 * the outcome everybody wanted.
 */
export function reviewLoans(
  deductions: readonly HrDeduction[],
  period: { year: number; month: number },
  opts: {
    nameOf?: (staffId: string) => string | undefined;
    slowMonths?: number;
  } = {},
): LoanConcern[] {
  const slow = opts.slowMonths ?? LOAN_SLOW_MONTHS;
  const out: LoanConcern[] = [];

  for (const d of deductions) {
    if (d.status && d.status !== 'active') continue;
    const cfg = DEDUCTION_TYPES[d.type];
    if (!cfg) continue;

    /* A BANK LOAN CARRIES NO BALANCE, AND STILL STOPS SILENTLY.
     *
     * bank_scheme has no balanceKey on purpose — the bank deals with the
     * borrower and simply tells the school what to deduct, so the school
     * never knows what is owed. But the failure that started all of this
     * applies to it just the same: clear the amount and the deduction
     * vanishes from the payslip with nothing said. Peggy Modise, Thabiso
     * Ikobe and Junior Bazangaphe were all bank schemes.
     *
     * There is no balance to report and no payoff to project, so the only
     * question worth asking is whether an amount is set at all. */
    if (!cfg.balanceKey) {
      if (cfg.group !== 'loan') continue;           // not a loan of any kind
      if (round2(instalmentForPeriod(d, period)) > 0) continue;
      out.push({
        deductionId: d.id,
        staffId: d.staffId,
        staffName: opts.nameOf?.(d.staffId),
        type: d.type,
        label: cfg.label,
        balance: 0,
        instalment: 0,
        monthsToClear: null,
        kind: 'no-instalment',
        severity: 'action',
        message: 'No monthly amount is set, so this deduction will not appear on the payslip '
          + 'at all. The school is not told the balance on this kind of loan, so nothing else '
          + 'will notice it has stopped.',
      });
      continue;
    }

    /* WHAT IS STILL OWED GOING INTO THIS RUN, asked of the engine rather
       than read off the record. deductionOpeningBalance is the ORIGINAL
       advance; `values.remainingBalance` is a stored figure that nothing
       decrements. Only deductionForPeriod walks the months actually repaid
       and the top-ups taken since, so only it knows the balance today. */
    const res = deductionForPeriod(d, period);
    const taken = round2(res.amount);
    const balance = round2((res.remainingAfter ?? 0) + taken);
    if (balance <= 0.005) continue;                 // finished; nothing to say

    /* The repayment the record is SET UP for, which is the thing being
       reviewed. `taken` is only what this particular run does with it. */
    const configured = round2(instalmentForPeriod(d, period));

    const base = {
      deductionId: d.id,
      staffId: d.staffId,
      staffName: opts.nameOf?.(d.staffId),
      type: d.type,
      label: cfg.label,
      balance,
      instalment: configured,
    };

    if (configured <= 0) {
      out.push({
        ...base, monthsToClear: null, kind: 'no-instalment', severity: 'action',
        message: 'No monthly repayment amount is set, so nothing will ever be collected '
          + 'against this balance.',
      });
      continue;
    }

    const months = Math.ceil(balance / configured);
    if (months > slow) {
      out.push({
        ...base, monthsToClear: months, kind: 'very-slow', severity: 'action',
        message: `At ${configured.toFixed(2)} a month this balance takes ${months} months to clear. `
          + 'Check the repayment amount — it is the usual sign of a figure entered in the wrong box.',
      });
      continue;
    }

    /* Configured sensibly, but this run will not take it, because the record
       states a later first month. Since a stated month now beats the cut-off,
       that is a deliberate choice by whoever entered it rather than something
       the engine did on its own — but it is still the reason a deduction the
       office is expecting will not appear, so it is said out loud before they
       go looking for it. */
    if (taken <= 0) {
      out.push({
        ...base, monthsToClear: months, kind: 'starts-later', severity: 'note',
        message: 'Set to begin after this month, so the first repayment falls in a later '
          + 'payroll rather than this one. Nothing to fix.',
      });
    }
  }

  /* Worst first: nothing being collected outranks something collected too
     slowly, notes last, and within each the largest balance at risk. */
  const rank: Record<LoanConcernKind, number> = { 'no-instalment': 0, 'very-slow': 1, 'starts-later': 2 };
  return out.sort((a, b) => rank[a.kind] - rank[b.kind] || b.balance - a.balance);
}

/** One line for the top of the review: what the run would leave uncollected. */
export function loanConcernTotals(concerns: readonly LoanConcern[]): {
  count: number; needingAction: number; balanceAtRisk: number; collectingNothing: number;
} {
  const action = concerns.filter((c) => c.severity === 'action');
  const collectingNothing = concerns.filter((c) => c.kind === 'no-instalment');
  return {
    count: concerns.length,
    needingAction: action.length,
    /* Only the balances no arrangement will ever recover. A loan repaying
       too slowly is being collected, and one waiting on the cut-off starts
       next month; neither belongs in a figure the office reads as lost. */
    balanceAtRisk: round2(collectingNothing.reduce((a, c) => a + c.balance, 0)),
    collectingNothing: collectingNothing.length,
  };
}
